import React, { useCallback, useEffect, useRef, useState } from "react";

import { ExploreScaleHero } from "./ExploreScaleHero";


import { ExploreNpsPatternSelector } from "./ExploreNpsPatternSelector";

import { PracticeStaff } from "../practice/PracticeStaff";

import { PracticeSurface } from "../practice/PracticeSurface";

import { TaskCreatorButton } from "../TaskCreatorButton";

import { serializeScaleState } from "../../lib/taskEngine";

import type { NoteName, NeckPosition } from "../../lib/guitarEngine";

import type { NeckConnection } from "../practice/PracticeSurface";

import type { FretboardPattern } from "../../lib/fingeringEngine";

import type { ExploreNpsSelection } from "../../lib/explore3nps";



export interface ExploreImmersiveCanvasProps {

  rootNote: NoteName;

  scaleLabel: string;

  scaleType: string;

  notes: readonly NoteName[];

  scaleOptions: readonly { value: string; label: string }[];

  noteCount: number;

  degreeCount: number;

  onPrevNote: () => void;

  onNextNote: () => void;

  onPrevScale: () => void;

  onNextScale: () => void;

  onSelectNote: (n: NoteName) => void;

  onSelectScale: (value: string) => void;

  octaves: number;

  direction: "asc" | "desc" | "both";

  pattern: string;

  activeNote: { string: number; fret: number } | null;

  playbackSequence: Array<{ note: NoteName; string: number; fret: number }>;

  currentSeqIndex: number;

  highlightedPositions: NeckPosition[];

  onFretClick: (stringNum: number, fret: number, note: NoteName) => void;

  surface: "keyboard" | "fretboard";

  stringOrder: "tab" | "physical";

  leftHanded: boolean;

  onStringOrderToggle: () => void;

  onLeftHandedToggle: () => void;

  connections: NeckConnection[];

  dimmedKeys?: Set<string>;

  focusLevel: 1 | 2 | 3 | 4 | 5;

  practiceFeedback?: "success" | "fail";

  successPositions?: Set<string>;

  groupedPositions?: Map<string, { speciesFill: string; groupHalos: string[] }>;

  showFingerBadges?: boolean;

  mnemonicTip?: {

    arrow: string;

    label: string;

    fromString: number;

    fromFret: number;

    toString: number;

    toFret: number;

  } | null;

  npsPatterns: readonly FretboardPattern[];

  npsSelection: ExploreNpsSelection;

  onNpsSelectionChange: (id: ExploreNpsSelection) => void;

  spotlightKeys?: Set<string>;

  staffHighlightNote?: { string: number; fret: number } | null;

  hoverNote?: { string: number; fret: number } | null;

  isPlaying?: boolean;

  onFretHover?: (stringNum: number, fret: number, note: NoteName) => void;

  onFretLeave?: () => void;

}



export const ExploreImmersiveCanvas: React.FC<ExploreImmersiveCanvasProps> = ({

  rootNote,

  scaleLabel,

  scaleType,

  notes,

  scaleOptions,

  noteCount,

  degreeCount,

  onPrevNote,

  onNextNote,

  onPrevScale,

  onNextScale,

  onSelectNote,

  onSelectScale,

  octaves,

  direction,

  pattern,

  activeNote,

  playbackSequence,

  currentSeqIndex,

  highlightedPositions,

  onFretClick,

  surface,

  stringOrder,

  leftHanded,

  onStringOrderToggle,

  onLeftHandedToggle,

  connections,

  dimmedKeys,

  focusLevel,

  practiceFeedback,

  successPositions,

  groupedPositions,

  showFingerBadges,

  mnemonicTip,

  npsPatterns,

  npsSelection,

  onNpsSelectionChange,

  spotlightKeys,

  staffHighlightNote,

  hoverNote,

  isPlaying = false,

  onFretHover,

  onFretLeave,

}) => {

  const fretboardRef = useRef<HTMLDivElement>(null);

  const [boardWidth, setBoardWidth] = useState(1400);



  const measureBoard = useCallback(() => {

    const el = fretboardRef.current;

    if (!el) return;

    setBoardWidth(Math.max(640, el.clientWidth - 16));

  }, []);



  useEffect(() => {

    measureBoard();

    const el = fretboardRef.current;

    if (!el) return;

    const ro = new ResizeObserver(measureBoard);

    ro.observe(el);

    return () => ro.disconnect();

  }, [measureBoard]);

  return (

    <div className="flex flex-col w-full max-w-none overflow-x-hidden flex-1 pb-1 min-h-0">

      {/* Encabezado + selector 3NPS */}

      <div className="px-12 pt-14 w-full shrink-0">

        <div className="flex justify-between items-baseline w-full border-b border-white/10 pb-0 mb-0">

          <div className="min-w-0 flex-1">

            <ExploreScaleHero

              immersive

              rootNote={rootNote}

              scaleLabel={scaleLabel}

              scaleType={scaleType}

              notes={notes}

              scaleOptions={scaleOptions}

              noteCount={noteCount}

              degreeCount={degreeCount}

              onPrevNote={onPrevNote}

              onNextNote={onNextNote}

              onPrevScale={onPrevScale}

              onNextScale={onNextScale}

              onSelectNote={onSelectNote}

              onSelectScale={onSelectScale}

            />

            <ExploreNpsPatternSelector

              selection={npsSelection}

              onSelect={onNpsSelectionChange}

              patterns={npsPatterns}

            />

          </div>

          <TaskCreatorButton

            type="scale"

            variant="immersive"

            title={`Escala ${rootNote} ${scaleLabel}`}

            description={`Patrón: ${pattern}, ${octaves} octava(s), ${direction}`}

            config={serializeScaleState(rootNote, scaleType, octaves, direction, pattern)}

          />

        </div>

      </div>



      {/* Pentagrama atmosférico — pegado al mástil */}

      <div className="px-12 pb-1 flex flex-col w-full shrink-0">

        <div

          className="w-full text-left overflow-visible opacity-50 transition-opacity duration-500 ease-out hover:opacity-70 -mb-1"

        >

          <PracticeStaff
            compact
            atmospheric
            staticScaleOnly
            rootNote={rootNote}
            scaleType={scaleType}
            activeNote={staffHighlightNote}
            learnSurface
          />

        </div>

      </div>



      {/* Mástil */}

      <div className="mt-0 w-full px-4 pb-2 flex-1 min-h-[280px] flex flex-col relative">

        {mnemonicTip && (

          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 px-4 py-2 rounded-xl bg-[#2C2825]/90 text-white shadow-lg">

            <div className="flex items-center gap-2 text-xs font-sans font-semibold">

              <span className="text-lg">{mnemonicTip.arrow}</span>

              <span>

                {mnemonicTip.label}: {mnemonicTip.fromString} (tr. {mnemonicTip.fromFret}){" "}

                {mnemonicTip.arrow} {mnemonicTip.toString} (tr. {mnemonicTip.toFret})

              </span>

            </div>

          </div>

        )}



        <div ref={fretboardRef} className="w-full flex-1 min-h-[320px] max-w-none">

          <PracticeSurface

            className="w-full h-full min-h-[320px] max-w-none"

            highlightedPositions={highlightedPositions}

            activeNote={activeNote}

            onFretClick={onFretClick}

            onFretHover={onFretHover}

            onFretLeave={onFretLeave}

            showNoteNames

            showDegrees={surface === "keyboard"}

            showMappedKeyLabels={surface === "keyboard"}

            interactive

            stringOrder={stringOrder}

            showFingerBadges={showFingerBadges}

            hand="left"

            leftHanded={leftHanded}

            connections={connections}

            dimmedKeys={dimmedKeys}

            focusLevel={focusLevel as 1 | 2 | 3}

            practiceFeedback={practiceFeedback}

            successPositions={successPositions}

            groupedPositions={groupedPositions}

            visualStyle="wireframe"

            sizeTier="monumental"

            boardWidthOverride={surface === "fretboard" ? boardWidth : undefined}

            spotlightKeys={spotlightKeys}

            hoverNote={hoverNote}

            dimInactiveDuringPlayback={isPlaying}

          />

        </div>



        {surface === "fretboard" && (

          <div className="absolute top-2 right-6 flex flex-col gap-1.5 z-10">

            <button

              type="button"

              onClick={onStringOrderToggle}

              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans transition-all bg-black/35 backdrop-blur-md border border-white/12 text-slate-100 hover:bg-white/10"

              title={stringOrder === "physical" ? "Orden físico" : "Orden tab"}

            >

              {stringOrder === "physical" ? "|||" : "≡"}

            </button>

            <button

              type="button"

              onClick={onLeftHandedToggle}

              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-sans transition-all border ${

                leftHanded

                  ? "bg-emerald-400/20 text-emerald-200 border-emerald-400/40"

                  : "bg-black/35 border-white/12 text-slate-100 hover:bg-white/10"

              }`}

              title={leftHanded ? "Zurdo" : "Diestro"}

            >

              {leftHanded ? "←" : "→"}

            </button>

          </div>

        )}

      </div>

    </div>

  );

};


